import { debounce } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { SystemStyleObject } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, Outlet, useNavigate, useParams } from 'react-router-dom';

import {
  customerSelector,
  useListContactsInfiniteQuery,
  useListRecipientsInfiniteQuery,
} from '@/api/v1';
import { Clickable } from '@/components/atoms/Clickable';
import { ContactItem } from '@/components/molecules/ContactItem';
import { SearchBox } from '@/components/molecules/SearchBox';
import { MainScreen } from '@/components/organisms/MainScreen';
import { useTranslator } from '@/hooks/useTranslator';
import { ContactsDrawerContext } from '@/types/contactDrawerContext';
import { Contact } from '@/types/contacts';
import { Customer } from '@/types/customer';
import { Page } from '@/types/page';
import { RecipientResponse } from '@/types/recipient';
import { isEmptyNullOrUndefined } from '@/utility/isEmptyNullOrUndefined';

export const Contacts: React.FC = (): React.ReactElement => {
  const { data: customer = Customer.empty() } = useSelector(customerSelector);
  const { accountId, contactId } = useParams<ContactsDrawerContext>();
  const navigate = useNavigate();
  const tr = useTranslator();

  const [ownAccountsSearch, setOwnAccountsSearch] = React.useState('');
  const [recipientsSearch, setRecipientsSearch] = React.useState('');

  const {
    data: ownAccountsData,
    fetchNextPage: fetchMoreOwnAccounts,
    hasNextPage: hasMoreOwnAccounts,
    isFetching: fetchingOwnAccounts,
  } = useListRecipientsInfiniteQuery(
    {
      beneficiaryId: customer.beneficiaryId,
      belongsToBeneficiary: true,
      size: 5,
      name: ownAccountsSearch.trim() || undefined,
    },
    { skip: isEmptyNullOrUndefined(customer.beneficiaryId) },
  );

  const {
    data: recipientsData,
    fetchNextPage: fetchMoreRecipients,
    hasNextPage: hasMoreRecipients,
    isFetching: fetchingRecipients,
  } = useListContactsInfiniteQuery(
    {
      beneficiaryId: customer.beneficiaryId,
      size: 25,
      name: recipientsSearch.trim() || undefined,
    },
    { skip: isEmptyNullOrUndefined(customer.beneficiaryId) },
  );

  const ownAccounts = React.useMemo(
    (): readonly RecipientResponse[] =>
      ownAccountsData?.pages.flatMap(
        (page: Page<RecipientResponse>): readonly RecipientResponse[] => page.content,
      ) ?? [],
    [ownAccountsData],
  );

  const recipients = React.useMemo(
    (): readonly Contact[] =>
      recipientsData?.pages.flatMap((page: Page<Contact>): readonly Contact[] => page.content) ??
      [],
    [recipientsData],
  );

  const handleOwnAccountsSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setOwnAccountsSearch(event.target.value);
    },
    [],
  );

  const handleRecipientsSearch = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      setRecipientsSearch(event.target.value);
    },
    [],
  );

  const debouncedHandleOwnAccountsSearch = React.useMemo(
    (): ReturnType<typeof debounce> => debounce(handleOwnAccountsSearch, 300),
    [handleOwnAccountsSearch],
  );

  const debouncedHandleRecipientsSearch = React.useMemo(
    (): ReturnType<typeof debounce> => debounce(handleRecipientsSearch, 300),
    [handleRecipientsSearch],
  );

  React.useEffect((): VoidFunction => {
    return (): void => {
      debouncedHandleOwnAccountsSearch.clear();
      debouncedHandleRecipientsSearch.clear();
    };
  }, [debouncedHandleOwnAccountsSearch, debouncedHandleRecipientsSearch]);

  const onAccountClick = React.useCallback(
    (accountId: string): void => {
      navigate(`account/${accountId}/details`);
    },
    [navigate],
  );

  const onRecipientClick = React.useCallback(
    (recipientId: string): void => {
      navigate(`recipient/${recipientId}/details`);
    },
    [navigate],
  );

  const onLoadMoreOwnAccounts = React.useCallback((): void => {
    fetchMoreOwnAccounts();
  }, [fetchMoreOwnAccounts]);

  const onLoadMoreRecipients = React.useCallback((): void => {
    fetchMoreRecipients();
  }, [fetchMoreRecipients]);

  const outletContext = React.useMemo(
    (): ContactsDrawerContext => ({ contactId, accountId }),
    [accountId, contactId],
  );

  return (
    <MainScreen title={tr('CONTACTS')}>
      <Box position="absolute" top={0} right={0}>
        <Button variant="contained" size="compact" component={Link} to="new">
          {tr('ADD_NEW')}
        </Button>
      </Box>
      <Box display="flex" flexDirection="column" gap={3} mt={1}>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={4}>
              <Typography variant="h6">{tr('YOUR_ACCOUNTS')}</Typography>
              <Box minWidth={260}>
                <SearchBox
                  fullWidth={true}
                  placeholder={tr('SEARCH_BY_CURRENCY_OR_RAIL')}
                  onChange={debouncedHandleOwnAccountsSearch}
                />
              </Box>
            </Box>
            {ownAccounts.length === 0 && !fetchingOwnAccounts ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                {tr('NO_ITEMS_FOUND')}
              </Typography>
            ) : (
              <Box sx={styles.accountsGrid}>
                {ownAccounts.map(
                  (recipient: RecipientResponse): React.ReactElement => (
                    <Clickable key={recipient.id} data={recipient.id} onClick={onAccountClick}>
                      <ContactItem
                        recipients={[recipient]}
                        title={recipient.individualName ?? recipient.businessName ?? ''}
                      />
                    </Clickable>
                  ),
                )}
              </Box>
            )}
            {hasMoreOwnAccounts && ownAccounts.length > 0 && (
              <Box display="flex" justifyContent="center" pt={3}>
                <Button
                  variant="dark"
                  size="compact"
                  onClick={onLoadMoreOwnAccounts}
                  disabled={fetchingOwnAccounts}>
                  {tr('LOAD_MORE')}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
        <Paper elevation={1}>
          <Box px={4} py={5}>
            <Box display="flex" alignItems="center" justifyContent="space-between" pb={4}>
              <Typography variant="h6">{tr('RECIPIENTS')}</Typography>
              <Box minWidth={260}>
                <SearchBox
                  fullWidth={true}
                  placeholder={tr('SEARCH_BY_NAME')}
                  onChange={debouncedHandleRecipientsSearch}
                />
              </Box>
            </Box>
            {recipients.length === 0 && !fetchingRecipients ? (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
                {tr('NO_ITEMS_FOUND')}
              </Typography>
            ) : (
              <Box sx={styles.recipientsGrid}>
                {recipients.map(
                  (contact: Contact): React.ReactElement => (
                    <Clickable key={contact.id} data={contact.id} onClick={onRecipientClick}>
                      <ContactItem
                        key={contact.id}
                        recipients={contact.recipients}
                        title={Contact.getName(contact)}
                      />
                    </Clickable>
                  ),
                )}
              </Box>
            )}
            {hasMoreRecipients && recipients.length > 0 && (
              <Box display="flex" justifyContent="center" pt={3}>
                <Button
                  variant="dark"
                  size="compact"
                  onClick={onLoadMoreRecipients}
                  disabled={fetchingRecipients}>
                  {tr('LOAD_MORE')}
                </Button>
              </Box>
            )}
          </Box>
        </Paper>
        <Box height={30} />
      </Box>

      {/* For modals */}
      <Outlet context={outletContext} />
    </MainScreen>
  );
};

const styles: Record<string, SystemStyleObject> = {
  recipientsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 2,
  },
  accountsGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: 2,
  },
};
